/**
 * DashboardPage -- Fleet overview.
 *
 * Shows a grid of MachineCard components with online/offline status,
 * auto-refreshing every 30 seconds. Summary stats at top: total, online, offline.
 * Alert badges shown on machines with active alerts.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getRevenueSummary, getFeedbackSummary, getCodeSpace, type RevenueSummary, type FeedbackSummary, type FeedbackTotals, type CodeSpace } from '../api/client';
import { useMachines } from '../hooks/useMachines';
import { useAlerts } from '../hooks/useAlerts';
import { MachineCard } from '../components/MachineCard';
import { FeedbackBar } from '../components/FeedbackBar';
import { RevenueChart } from '../components/RevenueChart';

export function DashboardPage({ role }: { role?: string }) {
  const { machines, loading, error, refresh } = useMachines();
  const { alerts } = useAlerts();
  const [revenue, setRevenue] = useState<RevenueSummary | null>(null);

  // Занятость 8-значных кодов — служебная цифра только для суперадмина:
  // пока это доли процента, коды уникальны навсегда; когда дорастёт до единиц
  // процентов, пора включать окно уникальности (см. session-code.ts).
  const [codeSpace, setCodeSpace] = useState<CodeSpace | null>(null);
  useEffect(() => {
    if (role !== 'superadmin') return;
    let alive = true;
    getCodeSpace()
      .then((r) => { if (alive) setCodeSpace(r); })
      .catch(() => { if (alive) setCodeSpace(null); });
    return () => { alive = false; };
  }, [role]);

  // Деньги — первое, что хотят видеть на главной. Считаются тем же правилом,
  // что и выгрузка отчёта (без возвратов и без песочницы), чтобы цифры сходились.
  useEffect(() => {
    let alive = true;
    getRevenueSummary()
      .then((r) => { if (alive) setRevenue(r); })
      .catch(() => { if (alive) setRevenue(null); });
    return () => { alive = false; };
  }, []);

  // Отзывы покупателей за 30 дней — общий итог и по машинам (в карточки).
  const [feedback, setFeedback] = useState<FeedbackSummary | null>(null);
  useEffect(() => {
    let alive = true;
    getFeedbackSummary(30)
      .then((r) => { if (alive) setFeedback(r); })
      .catch(() => { if (alive) setFeedback(null); });
    return () => { alive = false; };
  }, []);
  const feedbackByMachine = new Map<number, FeedbackTotals>(
    (feedback?.byMachine ?? []).map((m) => [m.machineId, m]),
  );

  const onlineCount = machines.filter((m) => m.isOnline).length;
  const offlineCount = machines.filter((m) => !m.isOnline).length;

  // Build a map of machineId -> active alert count
  const alertsByMachine = new Map<number, { count: number; types: string[] }>();
  for (const alert of alerts) {
    const existing = alertsByMachine.get(alert.machineId);
    if (existing) {
      existing.count++;
      if (!existing.types.includes(alert.alertType)) existing.types.push(alert.alertType);
    } else {
      alertsByMachine.set(alert.machineId, { count: 1, types: [alert.alertType] });
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6" style={{ flexWrap: 'wrap', gap: 8 }}>
        <h2>Fleet Overview</h2>
        <button className="btn btn-secondary btn-sm" onClick={refresh} disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* Деньги сверху: сегодня / 7 / 30 дней + разбивка по машинам за месяц. */}
      {revenue && (
        <div className="mb-6">
          <div className="dashboard-stats" style={{ marginBottom: 12 }}>
            <RevenueTile label="Today" money={revenue.today.revenueCzk} orders={revenue.today.orders} big />
            <RevenueTile label="Last 7 days" money={revenue.week.revenueCzk} orders={revenue.week.orders} />
            <RevenueTile label="Last 30 days" money={revenue.month.revenueCzk} orders={revenue.month.orders} />
            {revenue.bestDay && (
              <RevenueTile
                label="Best day ever"
                money={revenue.bestDay.revenueCzk}
                orders={revenue.bestDay.orders}
                note={formatDay(revenue.bestDay.day)}
              />
            )}
          </div>
          {revenue.daily && revenue.daily.length > 0 && (
            <div className="card" style={{ marginBottom: 12 }}>
              <div className="card-body" style={{ padding: '12px 20px' }}>
                <div className="flex items-center justify-between" style={{ marginBottom: 8, flexWrap: 'wrap', gap: 4 }}>
                  <div className="text-muted" style={{ fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Revenue per day · 30 days
                  </div>
                  {revenue.allTime && (
                    <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                      All time: <b style={{ color: 'var(--color-text)' }}>{revenue.allTime.revenueCzk} Kč</b> · {revenue.allTime.orders} orders
                    </div>
                  )}
                </div>
                <RevenueChart daily={revenue.daily} />
              </div>
            </div>
          )}
          {revenue.byMachineMonth.length > 0 && (
            <div className="card">
              <div className="card-body" style={{ padding: '12px 20px' }}>
                <div className="text-muted" style={{ fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
                  Revenue by machine · 30 days
                </div>
                {revenue.byMachineMonth.map((m) => (
                  <div key={String(m.machineId)}
                       style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '0.9rem' }}>
                    <span>
                      {m.machineId != null
                        ? <Link to={`/machines/${m.machineId}`} style={{ color: 'inherit' }}>{m.machineName ?? m.machineId}</Link>
                        : <span className="text-muted">web only (no machine)</span>}
                    </span>
                    <span style={{ fontWeight: 600 }}>
                      {m.revenueCzk} Kč <span className="text-muted" style={{ fontWeight: 400 }}>· {m.orders}</span>
                    </span>
                  </div>
                ))}
                <div style={{ marginTop: 8 }}>
                  <Link to="/reports" style={{ fontSize: '0.85rem' }}>Full report →</Link>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {feedback && feedback.totals.total > 0 && (
        <div className="card mb-6">
          <div className="card-body" style={{ padding: '12px 20px' }}>
            <div className="flex items-center justify-between" style={{ marginBottom: 6 }}>
              <div className="text-muted" style={{ fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Customer reviews · 30 days · {feedback.totals.total}
              </div>
              <Link to="/feedback" style={{ fontSize: '0.85rem' }}>All reviews →</Link>
            </div>
            <FeedbackBar totals={feedback.totals} />
          </div>
        </div>
      )}

      {/* Summary stats */}
      {machines.length > 0 && (
        <div className="dashboard-stats mb-6">
          <div className="stat-card card">
            <div className="card-body" style={{ padding: '16px 20px' }}>
              <div className="text-muted" style={{ fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
                Total Machines
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>
                {machines.length}
              </div>
            </div>
          </div>
          <div className="stat-card card">
            <div className="card-body" style={{ padding: '16px 20px' }}>
              <div className="text-muted" style={{ fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
                Online
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-online)' }}>
                {onlineCount}
              </div>
            </div>
          </div>
          <div className="stat-card card">
            <div className="card-body" style={{ padding: '16px 20px' }}>
              <div className="text-muted" style={{ fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
                Offline
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, color: offlineCount > 0 ? 'var(--color-offline)' : 'var(--color-text)' }}>
                {offlineCount}
              </div>
            </div>
          </div>
          {alerts.length > 0 && (
            <div className="stat-card card">
              <div className="card-body" style={{ padding: '16px 20px' }}>
                <div className="text-muted" style={{ fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
                  Active Alerts
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#ef4444' }}>
                  {alerts.length}
                </div>
              </div>
            </div>
          )}
          {codeSpace && (
            <div className="stat-card card" title="8-digit order codes ever issued. Unique forever; when this nears a few percent, switch to a uniqueness window (session-code.ts).">
              <div className="card-body" style={{ padding: '16px 20px' }}>
                <div className="text-muted" style={{ fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
                  Codes used ★
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>
                  {codeSpace.used.toLocaleString('en-US')}
                  <span className="text-muted" style={{ fontSize: '0.9rem', fontWeight: 400, marginLeft: 6 }}>
                    of 100M · {codeSpace.usedPct < 0.01 ? '<0.01' : codeSpace.usedPct.toFixed(2)}%
                  </span>
                </div>
                <div className="text-muted" style={{ fontSize: '0.78rem', marginTop: 2 }}>
                  {codeSpace.last30Days.toLocaleString('en-US')} in 30 days
                  {codeSpace.yearsToFivePct != null && ` · 5% in ~${codeSpace.yearsToFivePct} y`}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="card mb-6">
          <div className="card-body">
            <div style={{ color: 'var(--color-offline)', fontWeight: 500 }}>
              Error: {error}
            </div>
            <button className="btn btn-secondary btn-sm mt-4" onClick={refresh}>
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Loading state */}
      {loading && machines.length === 0 && !error && (
        <div className="card">
          <div className="card-body">
            <div className="empty-state">
              <h3>Loading machines...</h3>
              <p className="text-muted">Fetching status data from the server.</p>
            </div>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!loading && machines.length === 0 && !error && (
        <div className="card">
          <div className="card-body">
            <div className="empty-state">
              <h3>No machines connected</h3>
              <p className="text-muted">
                Machines will appear here once they start sending status data.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Machine grid */}
      {machines.length > 0 && (
        <div className="machine-grid">
          {machines.map((machine) => (
            <MachineCard
              key={machine.id}
              machine={machine}
              alertInfo={alertsByMachine.get(machine.machineId)}
              feedback={feedbackByMachine.get(machine.machineId)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** '2026-10-21' → «21 Oct 2026» в локали браузера. */
function formatDay(day: string): string {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function RevenueTile({ label, money, orders, big, note }: {
  label: string; money: number; orders: number; big?: boolean; note?: string;
}) {
  return (
    <div className="stat-card card">
      <div className="card-body" style={{ padding: '16px 20px' }}>
        <div className="text-muted" style={{ fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
          {label}
        </div>
        <div style={{ fontSize: big ? '1.9rem' : '1.5rem', fontWeight: 700 }}>{money} Kč</div>
        <div className="text-muted" style={{ fontSize: '0.8rem' }}>{orders} orders{note ? ` · ${note}` : ''}</div>
      </div>
    </div>
  );
}
